import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Briefcase, Trash2, RefreshCw, CheckCircle, Eye, Edit } from 'lucide-react';

const PMPOfficeManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pmp-office`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load PMP-Office inspections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteInspection = (inspection) => {
    setInspectionToDelete(inspection);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!inspectionToDelete) return;
    
    try {
      await axios.delete(`${API}/pmp-office/${inspectionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inspection deleted successfully');
      setShowDeleteDialog(false);
      setInspectionToDelete(null);
      fetchInspections();
    } catch (error) {
      toast.error('Failed to delete inspection');
      setShowDeleteDialog(false);
      setInspectionToDelete(null);
      console.error(error);
    }
  };

  const viewInspection = (inspection) => {
    setSelectedInspection(inspection);
    setShowDetailModal(true);
  };

  const editInspection = (inspection) => {
    navigate('/field/pmp-office', { state: { editData: inspection } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Briefcase className="h-6 w-6 text-purple-600" />
          <h2 className="text-2xl font-bold">PMP - Office Management</h2>
        </div>
        <Button onClick={fetchInspections} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mb-4">
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{inspections.length}</p>
                <p className="text-sm text-gray-500">Total Inspections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{inspections.filter(i => i.area_type === 'lobby').length}</p>
                <p className="text-sm text-gray-500">Lobby Inspections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{inspections.filter(i => i.area_type === 'office_space').length}</p>
                <p className="text-sm text-gray-500">Office Space Inspections</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {inspections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No PMP-Office inspections found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {inspections.map((inspection) => (
            <Card key={inspection.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{inspection.building_name}</CardTitle>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Address:</strong> {inspection.address}</span>
                      <span>•</span>
                      <span><strong>Type:</strong> <span className="capitalize">{inspection.area_type.replace('_', ' ')}</span></span>
                      <span>•</span>
                      <span><strong>Date:</strong> {_fmtDate(inspection.date_of_inspection)}</span>
                    </div>
                  </div>
                  {inspection.signature && (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Signed
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>Inspector:</strong> {inspection.inspector_name}</p>
                  {inspection.floor_number && (
                    <p className="text-sm text-gray-700"><strong>Floor:</strong> {inspection.floor_number}</p>
                  )}
                </div>
                
                {inspection.additional_notes && (
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-700 mb-1"><strong>Additional Notes:</strong></p>
                    <p className="text-sm text-gray-600">{inspection.additional_notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-xs text-gray-500">
                    <span>Submitted: {_fmtDateTime(inspection.submitted_at)}</span>
                    {inspection.photos && inspection.photos.length > 0 && (
                      <span className="ml-4">📸 {inspection.photos.length} photos</span>
                    )}
                    {inspection.forward_to_operations && (
                      <span className="ml-4">
                        <Badge variant="outline" className="text-orange-600 text-xs">Forwarded to Operations</Badge>
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewInspection(inspection)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editInspection(inspection)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteInspection(inspection)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>PMP Office Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Building Name</p>
                  <p className="text-base">{selectedInspection.building_name}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Area Type</p>
                  <p className="text-base capitalize">{selectedInspection.area_type.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Address</p>
                  <p className="text-base">{selectedInspection.address}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Inspection Date</p>
                  <p className="text-base">{_fmtDate(selectedInspection.date_of_inspection)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Inspector</p>
                  <p className="text-base">{selectedInspection.inspector_name}</p>
                </div>
                {selectedInspection.floor_number && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Floor Number</p>
                    <p className="text-base">{selectedInspection.floor_number}</p>
                  </div>
                )}
              </div>

              {selectedInspection.additional_notes && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Additional Notes</p>
                  <p className="text-base p-3 bg-gray-50 rounded">{selectedInspection.additional_notes}</p>
                </div>
              )}

              {selectedInspection.photos && selectedInspection.photos.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Photos ({selectedInspection.photos.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedInspection.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-full h-32 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}

              {selectedInspection.signature && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Signature</p>
                  <img src={selectedInspection.signature} alt="Signature" className="border rounded p-2" style={{maxHeight: '100px'}} />
                </div>
              )}

              <div className="text-sm text-gray-500 pt-4 border-t">
                Submitted: {_fmtDateTime(selectedInspection.submitted_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete the inspection for "{inspectionToDelete?.building_name}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default PMPOfficeManagement;
